import * as React from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { Button, DropdownMenu, DropdownToggle, Modal, ModalBody, ModalFooter, ModalHeader, UncontrolledDropdown, DropdownItem, Input } from 'reactstrap';
import { BrokerClient, BrokerModel, BrokerRefModel, ConversionClient } from '../../../../../client/ApiClient';
import { DataCell, DataRow, DataTable, DataTableButton } from '../../../../basic/DataTable';
import StageActionToolbar from '../generic/StageActionToolbar';
import { RangePaginationState, RangePagination, paginate } from 'components/basic/Pagination';
import { stagingPageUpdate, stagingPageSetPrimary, stagingPageCopyMappings, StagingPageCopyMappingsItem } from '../generic/Abstractions'
import { AssignRowStatus, RowStatus } from '../generic';
import { regex } from '../../Constants';

import '../../../../../styles/Icons.scss';
import DataListInput from '../../../../basic/DataListInput';
import { filterColumns, sort } from '../generic/EntityFunctions';

interface BrokerStagingPageArgs {
    conversionId: string,
}

type BrokerStagingPageProps = RouteComponentProps<BrokerStagingPageArgs>;

interface BrokerStagingPageState {
    items: (BrokerModel
        & StagingPageCopyMappingsItem)[]
    itemsByDestinationCodes: BrokerModel[];
    filter: string
    destinationCodes: BrokerRefModel[];
    sort: boolean;
    sortColumn: string;
    mapped: boolean;
    notMapped: boolean;
    dropped: boolean;
    modal: boolean;
    repeatedCode: string;
    lastValue: string;
    canCancel: boolean;
    zero: boolean;
    primaryList: {
        destinationCode?: string,
        pItems?: BrokerModel[]
    }[];

    pagination: RangePaginationState;
    buttonLock: boolean;
    includeAddToMain: boolean;
    filteredColumn: string;
}

export class BrokerStagingPage extends React.Component<BrokerStagingPageProps, BrokerStagingPageState>{
    private client: BrokerClient = new BrokerClient()
    private conversionClient: ConversionClient = new ConversionClient();

    private readonly copyMappings = stagingPageCopyMappings(this);

    constructor(props: BrokerStagingPageProps) {
        super(props)
        this.state = {
            items: [],
            itemsByDestinationCodes: [],
            filter: '',
            destinationCodes: [],
            sort: true,
            sortColumn: '',
            mapped: true,
            notMapped: true,
            dropped: true,
            modal: false,
            repeatedCode: '',
            canCancel: false,
            zero: false,
            primaryList: [],
            lastValue: '',

            pagination: {
                pageSize: 25,
                pageNumber: 0,
            },
            buttonLock: true,
            includeAddToMain: false,
            filteredColumn: '',
        }
    }

    componentDidMount() {
        this.getItems();
        this.UIController();

        this.client.getRef(this.props.match.params.conversionId)
            .then(response => {
                if (response != null) {
                    this.setState({
                        destinationCodes: response,
                    })
                }
            })
            .catch(error => { })
    }

    setDefaults = (entity: BrokerModel) => new BrokerModel({
        ...entity,
        destinationCode: entity.destinationCode || ' ',
        destinationDescription: this.destinationName(entity.destinationCode || ''),
        isCompany: entity.isCompany || false,
        isNetCommission: entity.isNetCommission || false
    })

    getItems = () => {
        this.client.getAll(this.props.match.params.conversionId, '')
            .then((response) => {
                let items = response!;
                items = sort(items.map(this.setDefaults), this.state.sortColumn, this.state.sort, this.state.primaryList);
                this.updatePrimaryList(items)

                this.setState({
                    items
                })
            })
            .catch(error => { })
    }

    destinationName = (code: string) => {
        if (code) {
            const dc = this.state.destinationCodes.filter(ref => ref.sourceCode === code);
            const name = dc.length === 0 ? ' ' : dc[0].firstName === null ? dc[0].lastName : `${dc[0].firstName} ${dc[0].lastName}`
            return name
        } else {
            return ' '
        }
    }

    put(entity: BrokerModel) {
        stagingPageUpdate(this, this.client)
            .withSource('Broker', entity.sourceDescription!)
            .update(entity)
            .then(() => this.getItems())
            .catch(error => {
                const response = JSON.parse(error.response)
                console.log(response)
            })
    }

    updatePrimaryList(items: any[]) {
        let primaryList:
            {
                destinationCode?: string,
                pItems: BrokerModel[]
            }[] = [];

        items.forEach((item: BrokerModel) => {
            if (item.destinationCode && item.isAddToMain) {
                let primaryObject = primaryList.find(x => x.destinationCode!.trim() === item.destinationCode!.trim());
                if (!primaryObject) {
                    primaryObject = { destinationCode: item.destinationCode, pItems: [] }
                    primaryList.push(primaryObject);
                }
                if (!item.isDrop) primaryObject.pItems.push(item);
            }
        });
        primaryList = primaryList.filter(x => x.pItems!.length > 1 && x.pItems.some(p => p.isPrimary));
        this.setState({ primaryList })
    }

    getByDestinationCodes = (destinationCode: string) => {
        this.client.getByDestinationCode(this.props.match.params.conversionId, destinationCode)
            .then(response => {
                if (response != null) {
                    const itemsByDestinationCodes = response
                    this.setState({ itemsByDestinationCodes })
                }
            })
            .catch(error => { })
    }

    handleSearch = (filter: string) => {
        this.setState({ filter })
    }


    handleChange = (e: any) => {
        const dc = e.target.value;
        const id = e.target.name;

        const items = this.state.items;
        const i = this.state.items.findIndex(v => v.id === id);
        items[i].destinationCode = dc.toUpperCase().replace(regex.standard360, '');
        items[i].destinationDescription = this.destinationName(items[i].destinationCode!);

        this.setState({ items }, () => {
            if (items[i].destinationCode !== this.state.lastValue && this.state.lastValue.includes(this.state.filter) && this.state.filter !== '') this.put(items[i])
        });
    }

    handleCheckboxChange = (id: string, e: { target: { name: any, checked: any } }) => {
        const items = this.state.items.map(
            v =>
                v.id === id
                ? new BrokerModel({
                    ...v,
                    [e.target.name]: e.target.checked,
                })
                    : v
            )
        this.setState({ items });
        const value = items.find(item => item.id === id)?.destinationCode; 
        if (e.target.name === "isDrop" || value !== '') this.put(items.find(v => v.id === id)!)
    }

    handleRadioChange = (id: string) => {
        const items = this.state.items.map(
            x => x.id === id
                ? new BrokerModel({
                    ...x,
                    isCompany: !x.isCompany
                })
                : x)
        this.setState({ items });
        const value = items.find(item => item.id === id)?.destinationCode;
        if (value !== '') this.put(items.find(v => v.id === id)!)
    }

    sort(column: string) {
        const sorted = this.state.sortColumn === column ? !this.state.sort : true;
        const items = sort(this.state.items, column, sorted, this.state.primaryList)
        this.setState({ sort: sorted, sortColumn: column, items });
    }

    mappedCheck(check: string) {
        if (check === 'notMapped') {
            const notMapped = !this.state.notMapped
            this.setState({ notMapped })
        } else if (check === 'mapped') {
            const mapped = !this.state.mapped
            this.setState({ mapped })
        } else if (check === 'dropped') {
            const dropped = !this.state.dropped
            this.setState({ dropped })
        }
    }

    modalToggle = () => {
        const modal = !this.state.modal
        this.setState({ modal })
        if (modal) this.getByDestinationCodes(this.state.repeatedCode)
    }

    handleSelectDestination = (i: number) => {
        const itemsByDestinationCodes = this.state.itemsByDestinationCodes
        itemsByDestinationCodes.map((item, index) => {
            if (index === i) {
                item.isPrimary = true
            } else {
                item.isPrimary = false
            }
            return item;
        })

        this.setState({ itemsByDestinationCodes })
    }

    putPrimary = () => {
        const codes = this.state.itemsByDestinationCodes
        let num = 0
        codes.forEach(c => c.isPrimary ? num += 1 : null)
        if (num === 1) {
            codes.forEach(code => code.isPrimary ? this.updatePrimary(code.id) : null)
            this.modalToggle()
        } else {
            alert('Please only have one Source Code set to primary')
        }
    }

    updatePrimary(id: string) {
        const item = this.state.itemsByDestinationCodes.find(item => item.id === id)!;
        stagingPageSetPrimary(this, this.client)
            .withSource(item.sourceCode!)
            .setPrimary(id)
            .catch(() => { });
    }

    primarySelectionModalShowIfHasCode = (repeatedCode: string | undefined, canCancel: boolean) => {
        if (!repeatedCode) {
            alert('Please specify a destination code in order to set a primary record.');
        } else {
            this.primarySelectionModalShow(repeatedCode, canCancel);
        }
    }

    primarySelectionModalShow = (repeatedCode: string, canCancel: boolean) => {
        this.setState({
            modal: true,
            canCancel: canCancel,
            repeatedCode,
        })
        this.getByDestinationCodes(repeatedCode)
    }

    primarySelectionModalHide = () => {
        this.setState({ modal: false })
    }

    zero = () => {
        this.setState({ zero: !this.state.zero })
    }

    lockButton = (lock: boolean) => {
        this.setState({ buttonLock: lock })
    }

    DestinationCodes = (): any[] => {
        const arr = [];
        const dc = this.state.destinationCodes;
        if (dc.length > 0) {
            for (let i = 0; i < dc.length; i++) {
                const name = dc[i].firstName === null ? dc[i].lastName : `${dc[i].firstName} ${dc[i].lastName}`
                arr.push(<option
                    key={i}
                    data-value={dc[i].sourceCode?.toUpperCase()}
                >
                    {`${dc[i].sourceCode!.trim()} - ${name}`}
                </option>)
            }
        } else {
            arr.push(<option data-value={'readOnly'}> No Brokers Available in Target</option>)

        }
        return arr;
    }

    handleChangeDropDown = (item: BrokerModel, e: any) => {
        const i = this.state.items.findIndex(v => v.id === item.id);
        const items = this.state.items.map(
            v => v.id === item.id
                ? new BrokerModel({
                    ...v,
                    destinationCode: e.value
                })
                : v
        )
        this.setState({ items });
        if (item.destinationCode?.trim() !== items[i].destinationCode?.trim()) this.put(items[i])
    }

    UIController = () => {
        this.conversionClient.getUIController()
            .then((response) => {
                if (response) this.setState({ includeAddToMain: response!.addToMainEpic })
            })
            .catch(err => console.log(err))
    }

    showDropDown = (item: BrokerModel) => {
        const items = this.state.items.map(i => {
            return i.id === item.id
                ? new BrokerModel({
                    ...item,
                    isAddToMain: false,
                    destinationCode: '',
                })
                : i;
        })
        this.setState({ items })
    }

    addToMain = (item: BrokerModel) => {
        const items = this.state.items.map(i => {
            return i.id === item.id
                ? new BrokerModel({
                    ...item,
                    isAddToMain: true,
                    destinationCode: '',
                })
                : i;
        })
        this.setState({ items })
    }

    selectFilteredColumn = (filteredColumn: string) => {
        this.setState({ filteredColumn })
    }

    public render() {
        const conversionId = this.props.match.params.conversionId;
        const zero = this.state.zero ? "Show All" : "Hide 0 Rows"
        const filteredColumn = this.state.filteredColumn;
        const items = this.state.items
            .map(AssignRowStatus)
            .filter(i => ((filteredColumn === '' || filteredColumn === 'sourceCode') && i.sourceCode && i.sourceCode!.toLowerCase().includes(this.state.filter.trim().toLowerCase())) ||
                ((filteredColumn === '' || filteredColumn === 'sourceDescription') && i.sourceDescription && i.sourceDescription!.toLowerCase().includes(this.state.filter.trim().toLowerCase())) ||
                ((filteredColumn === '' || filteredColumn === 'destinationCode') && i.destinationCode && i.destinationCode!.toLowerCase().includes(this.state.filter.trim().toLowerCase())) ||
                ((filteredColumn === '' || filteredColumn === 'destinationDescription') && i.destinationDescription && i.destinationDescription!.toLowerCase().includes(this.state.filter.trim().toLowerCase())))
            .filter(item =>
                (item.mapped === RowStatus.NotMapped && this.state.notMapped)
                || (item.mapped === RowStatus.Mapped && this.state.mapped)
                || (item.mapped === RowStatus.Dropped && this.state.dropped))
            .filter(item => item.clientCount > 0 || item.policyCount > 0 || !this.state.zero || item.sourceCode!.includes('Default'));

        return (
            <React.Fragment>
                <StageActionToolbar
                    conversionId={conversionId}
                    sectionId='brokers'
                    handleSearch={this.handleSearch}
                    primaryList={this.state.primaryList}
                    getItems={() => this.getItems()}
                    handleCopyMapping={this.copyMappings.submitMappings}
                    copyMap={true}
                    filter={this.state.filter}
                    lockButton={this.lockButton}
                    filterColumns={filterColumns(items)}
                    selectFilteredColumn={this.selectFilteredColumn}
                />
                <DataTable>
                    <thead>
                        <tr className='nonFullWidthRow'>
                            <th onClick={() => this.sort('sourceCode')}>Source Code<i className="fa fa-sort"></i></th>
                            <th onClick={() => this.sort('sourceDescription')}>Source Description<i className="fa fa-sort"></i></th>
                            <th onClick={() => this.sort('isDrop')} className='checkbox-column'>Exclude<i className="fa fa-sort"></i></th>
                            <th onClick={() => this.sort('clientCount')} className='checkbox-column'>Clients<i className="fa fa-sort"></i></th>
                            <th onClick={() => this.sort('policyCount')} className='checkbox-column'>Policies<i className="fa fa-sort"></i></th>
                            <th onClick={() => this.sort('destinationCode')} className='addToMainContainer'>Destination Code<i className="fa fa-sort"></i></th>
                            <th onClick={() => this.sort('isNetCommission')} className='checkbox-col'>Net Commission<i className="fa fa-sort"></i></th>
                            <th onClick={() => this.sort('isCompany')} className='custom-dataCell'>BrokerType<i className="fa fa-sort"></i></th>
                            {this.state.includeAddToMain && <th onClick={() => this.sort('isPrimary')} className='checkbox-column'>Primary<i className="fa fa-sort"></i></th>}
                            <th className='checkbox-column'>
                                <UncontrolledDropdown setActiveFromChild className="ismappedDropdown">
                                    <DropdownToggle tag="a" className="ismapped" caret>
                                        Mapped
                                    </DropdownToggle>
                                    <DropdownMenu>
                                        <DropdownItem className={!this.state.notMapped ? 'mappedChecked' : ''} onClick={() => this.mappedCheck('notMapped')}>&#x274C; Not Mapped</DropdownItem>
                                        <DropdownItem className={!this.state.mapped ? 'mappedChecked' : ''} onClick={() => this.mappedCheck('mapped')}>&#x2705; Mapped</DropdownItem>
                                        <DropdownItem className={!this.state.dropped ? 'mappedChecked' : ''} onClick={() => this.mappedCheck('dropped')}>&#x2796; Excluded</DropdownItem>
                                    </DropdownMenu>
                                </UncontrolledDropdown>
                            </th>
                            <th onClick={() => this.sort('isUserMapped')} className='checkbox-column'>Auto-Mapped<i className="fa fa-sort"></i></th>
                            <th className='merge-name-toggle'><label className="switch"><input type="checkbox" readOnly checked={this.state.zero} onClick={this.zero} /> <span className="slider round"></span></label>{zero}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {
                            paginate(items, this.state.pagination, (item, i) =>
                                <DataRow id={'broker' + i} key={i}>
                                    <DataCell>{item.sourceCode}</DataCell>
                                    <DataCell>{item.sourceDescription}</DataCell>
                                    <DataCell className='checkbox-column'>
                                        <input className='custom-checkbox' type='checkbox' name='isDrop' checked={item.isDrop} value={item.id} onChange={e => this.handleCheckboxChange(item.id, e)} disabled={this.state.buttonLock} />
                                    </DataCell>
                                    <DataCell className='center-aligning checkbox-column'>{!item.sourceCode!.includes('Default') && item.clientCount}</DataCell>
                                    <DataCell className='center-aligning checkbox-column'>{!item.sourceCode!.includes('Default') && item.policyCount}</DataCell>
                                    <DataCell className='addToMainContainer'>
                                        {this.state.includeAddToMain ?
                                            <>
                                                {
                                                    !item.isAddToMain ?
                                                        <div className={"addToMainEpic"}>
                                                            <DataListInput
                                                                id={`referenceId - ${i}`}
                                                                name={item.id.toString()}
                                                                value={item.destinationCode ? item.destinationCode.toUpperCase() : undefined}
                                                                onValueChange={event => this.handleChangeDropDown(item, event)}
                                                                disabled={this.state.buttonLock}
                                                            >
                                                                <option data-value='' />
                                                                {this.DestinationCodes()}
                                                            </DataListInput>
                                                        </div>
                                                        :
                                                        <Input className="addToMainEpic"
                                                            value={item.destinationCode}
                                                            name={item.id.toString()}
                                                            onChange={this.handleChange}
                                                            onFocus={(e) => (this.setState({ lastValue: e.target.value.trim() }))}
                                                            onBlur={() => { if (item.destinationCode !== this.state.lastValue) this.put(item) }}
                                                            maxLength={6}
                                                            autoComplete="off"
                                                            placeholder={"Add to Main - 6 max"}
                                                            disabled={this.state.buttonLock} />
                                                }
                                                {item.sourceCode !== 'DefaultBroker' &&
                                                    <DataTableButton
                                                        icon={item.isAddToMain ? 'list' : 'edit'}
                                                        id={"addToMain"}
                                                        title={item.isAddToMain ? 'Dropdown List' : 'Add To Main'}
                                                        action={() => item.isAddToMain ? this.showDropDown(item) : this.addToMain(item)}
                                                    />}
                                            </>
                                            :
                                            <DataListInput
                                                id={`referenceId - ${i}`}
                                                name={item.id.toString()}
                                                value={item.destinationCode ? item.destinationCode.toUpperCase() : undefined}
                                                onValueChange={event => this.handleChangeDropDown(item, event)}
                                                disabled={this.state.buttonLock}
                                            >
                                                <option data-value='' />
                                                {this.DestinationCodes()}
                                            </DataListInput>
                                        }
                                    </DataCell>
                                    <DataCell className='checkbox-col'>
                                        {item.sourceCode !== 'DefaultBroker' &&
                                            <input className='custom-checkbox'
                                                type='checkbox'
                                                name='isNetCommission'
                                                checked={item.isNetCommission}
                                                onChange={e => this.handleCheckboxChange(item.id, e)}
                                                disabled={this.state.buttonLock || !item.isAddToMain}
                                            />}
                                    </DataCell>
                                    <DataCell className='custom-dataCell'>
                                        {item.sourceCode !== 'DefaultBroker' &&
                                            <>
                                                <label className='type-style-broker'>
                                                    <input className='custom-radioButton' name={`company${i}`} type='radio' checked={item.isCompany} value={item.id} onChange={e => this.handleRadioChange(item.id)} disabled={this.state.buttonLock || !item.isAddToMain} />
                                                    Firm
                                                </label>
                                                <label className='type-style-broker'>
                                                    <input className='custom-radioButton' name={`company${i}`} type='radio' checked={!item.isCompany} value={item.id} onChange={e => this.handleRadioChange(item.id)} disabled={this.state.buttonLock || !item.isAddToMain} />
                                                    Individual
                                                </label>
                                            </>}    
                                    </DataCell>
                                    {this.state.includeAddToMain && <DataCell className='checkbox-column'
                                        itemProp={this.state.primaryList.some(p => p.destinationCode!.toUpperCase() === item.destinationCode!.toUpperCase()) && !item.isDrop
                                        ? item.isPrimary && item.isMapped
                                            ? 'primary'
                                            : 'non-primary' : ''}>
                                        <DataTableButton
                                            id={item.id + 'primary'}
                                            action={() => this.primarySelectionModalShowIfHasCode(item.destinationCode, true)}
                                            disabled={this.state.buttonLock}
                                            title='Click to set a primary record'
                                        >
                                            {item.isAddToMain ? this.state.primaryList.some(p => p.destinationCode!.toUpperCase() === item.destinationCode!.toUpperCase()) && !item.isDrop
                                                ? item.isPrimary && item.isMapped
                                                    ? <span className='mapped' itemProp='primary'>&#x2705;</span>
                                                    : <span className='mapped' itemProp='non-primary'>&#x274C;</span> : null: null}
                                        </DataTableButton>
                                    </DataCell>}
                                    <DataCell className='checkbox-column' itemProp={item.mapped === RowStatus.NotMapped ? 'unmapped' : item.mapped === RowStatus.Mapped ? 'mapped' : 'dropped'}>
                                        {item.mapped === RowStatus.NotMapped ? < span className='mapped' itemProp='unmapped'>&#x274C;</span> : item.mapped === RowStatus.Mapped ? < span className='mapped' itemProp='mapped'>&#x2705;</span> : < span className='mapped' itemProp='dropped'>&#x2796;</span>}
                                    </DataCell>
                                    <DataCell
                                        className='checkbox-column'
                                        itemProp={!item.isUserMapped && item.isMapped ? 'auto-mapped' : ''}
                                    >
                                        {!item.isUserMapped && item.isMapped ? < span className='mapped' itemProp='auto-mapped'>&#x2705;</span> : ''}
                                    </DataCell>
                                    <DataCell className='merge-name-toggle'>
                                    </DataCell>
                                </DataRow >
                            )
                        }
                    </tbody >
                </DataTable >
                <RangePagination
                    pageNumber={this.state.pagination.pageNumber}
                    pageSize={this.state.pagination.pageSize}
                    recordsCount={items.length}
                    onChangeIndex={index => this.setState({
                        pagination: {
                            ...this.state.pagination,
                            pageNumber: index,
                        }
                    })}
                />
                <div>
                    <Modal isOpen={this.state.modal}>
                        <ModalHeader>Please Set Primary of Destination: {this.state.repeatedCode}</ModalHeader>
                        <ModalBody>
                            <select
                                name='isPrimary'
                                className="custom-select"
                                onChange={(e) => this.handleSelectDestination(Number(e.target.value))}
                                id={this.state.repeatedCode.toString()}
                                key={this.state.repeatedCode}
                            >
                                {this.state.itemsByDestinationCodes.map((item, index) => (
                                    <option
                                        value={index}
                                        key={item.id}
                                        selected={item.isPrimary}
                                    >
                                        {item.sourceCode} - {item.sourceDescription}
                                    </option>
                                ))}
                            </select>
                        </ModalBody>
                        <ModalFooter>
                            {this.state.canCancel && <Button color="secondary" onClick={() => this.primarySelectionModalHide()}>Close</Button>}
                            <Button color="primary" onClick={this.putPrimary}>Submit</Button>
                        </ModalFooter>
                    </Modal>
                </div>
            </React.Fragment >
        );
    }
}